import { css } from '@emotion/react';
import { Button, semanticTokens, tokens } from '@flysas-tech/commercial-ui';
import { t } from 'i18n';
import { DateTime } from 'luxon';
import { ReactNode, useState } from 'react';
import { BOUND_SIDE } from 'shared';

const styles = {
  toggleMoreButton: css`
    color: ${semanticTokens.color.text.link};
  `,
  children: css`
    display: flex;
    flex-direction: column;
    gap: ${tokens.dimension['space-5']};
    margin: 0;
    padding: 0;
    list-style-type: none;
  `,
  toggleMoreButtonContainer: css`
    margin-top: ${tokens.dimension['space-6']};
    display: flex;
    justify-content: center;
  `,
};

type Props = {
  boundSide: BOUND_SIDE;
  journeyDate: string;
  rows: ReactNode[] | ReactNode;
};
export function BoundFlights({ rows, boundSide, journeyDate }: Props) {
  return <FlightsTable rows={rows} limit={10} boundSide={boundSide} journeyDate={journeyDate} />;
}

type FlightsTableProps = {
  limit: number;
  journeyDate: string;
  boundSide: BOUND_SIDE;
  rows: ReactNode[] | ReactNode;
};

function FlightsTable({ rows, journeyDate, limit, boundSide }: FlightsTableProps) {
  const [isOpen, setIsOpen] = useState(false);
  const formattedDate = DateTime.fromISO(journeyDate).toFormat('DDDD');

  if (!Array.isArray(rows)) return rows;

  const displayRows = rows.slice(0, limit);
  const hiddenRows = rows.slice(limit);
  const showButtons = rows.length > limit;

  const ariaLabel = `${
    boundSide === BOUND_SIDE.Outbound ? t('common:outBound') : t('common:inBound')
  }, ${formattedDate}`;

  return (
    <div>
      <ul css={styles.children} aria-label={ariaLabel}>
        {displayRows}
        {isOpen && hiddenRows}
      </ul>
      {showButtons && (
        <div css={styles.toggleMoreButtonContainer}>
          {isOpen ? (
            <Button
              data-testid={`upsell-show-less-flight-rows-${boundSide}`}
              variant="link"
              size="small"
              leadingIcon="Minus"
              css={styles.toggleMoreButton}
              onClick={() => setIsOpen(false)}
            >
              {t('flightList.showLess')}
            </Button>
          ) : (
            <Button
              data-testid={`upsell-show-more-flight-rows-${boundSide}`}
              variant="link"
              size="small"
              leadingIcon="PlusCircle"
              rotation={180}
              css={styles.toggleMoreButton}
              onClick={() => setIsOpen(true)}
            >
              {t('flightList.showMore', { count: hiddenRows.length })}
            </Button>
          )}
        </div>
      )}
    </div>
  );
}
